import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useCreateAgentDefinitionCompat as useCreateAgentDefinition, useCreateAgentGroupCompat as useCreateAgentGroup, useAgentDefinitionByIdCompat as useAgentDefinitionById, useAgentDefinitionConfigByIdCompat as useAgentDefinitionConfigById, useAgentOptionsCompat as useAgentOptions, useUpdateAgentDefinitionCompat as useUpdateAgentDefinition, useParamSchema } from '../hooks/useAgentDefinitions';
import { useAgentProcesses } from '../hooks/useAgentProcesses';
import type {
  PersonaAgentConfig as AgentConfig,
  AgentProcess,
  AgentCompletionParams,
  PersonaOptionsResponse,
  ModelCapabilitiesInfo,
  ModelLifecycleStatus,
  ParamRule,
  ProviderType,
  SupportedParamSpec,
} from '@localagent/sdk';
import { isConflict } from '@localagent/sdk';
import { Button } from '@localagent/ui/components/button';
import { Popover } from '@localagent/ui/components/popover';
import { PageShell } from '../components/layout/PageShell';
import { useCatalogSubscription } from '../hooks/useCatalogSubscription';
import { ModelCapabilitiesDisplay } from '../components/chat/ModelCapabilitiesDisplay';
import { AgentModelPicker } from '../components/agents/AgentModelPicker';
import {
  DroppedOverridesNotice,
  ModelDeprecationWarning,
  ParamOverrideNotice,
  SchemaStaleNotice,
} from '../components/agents/AgentParamNotices';
import { LoadingState } from '@localagent/ui/components/shogun-spinner';

/**
 * Params currently made unavailable by a `suppressed_by` rule.
 *
 * MECHANICAL ONLY: the client applies the rule shape the server already sent
 * (`kind` / `when` / `when_values` / `params`) and renders `message` VERBATIM.
 * It composes no explanation of its own — that is what makes it structurally
 * incapable of asserting a constraint the backend does not enforce.
 *
 * Mirrors `modelcatalog.SuppressedParams` (persona ADR-004; supersedes agents ADR-012).
 * A second, divergent predicate here would reintroduce the defect the feature
 * removed: an inert control whose text disagrees with the write-guard rejection.
 */
export interface SuppressionGroup {
  message: string;
  params: string[];
}

export interface SuppressionState {
  reasons: Record<string, string>;
  groups: SuppressionGroup[];
}

export function suppressedParamsFromRules(
  rules: ParamRule[],
  effective: Record<string, unknown>,
): SuppressionState {
  const reasons: Record<string, string> = {};
  const groups: SuppressionGroup[] = [];
  for (const rule of rules) {
    if (rule.kind !== 'suppressed_by' || !rule.when) continue;
    const governing = effective[rule.when];
    if (governing == null || governing === '') continue;
    if (rule.when_values?.length && !rule.when_values.includes(String(governing))) continue;
    const claimed: string[] = [];
    for (const key of rule.params) {
      if (key in reasons) continue;
      reasons[key] = rule.message;
      claimed.push(key);
    }
    if (claimed.length) groups.push({ message: rule.message, params: claimed });
  }
  return { reasons, groups };
}

/**
 * The server's suppression message, stated ONCE per rule below the param grid
 * (persona ADR-004).
 *
 * One `suppressed_by` rule listing several params used to render its message
 * under every one of them, so a single server sentence appeared verbatim two or
 * three times. The params it covers are named from `spec.label` — the client
 * still composes no explanation of its own and knows no param names.
 */
export function ParamSuppressionNotes({
  groups,
  specs,
}: {
  groups: SuppressionGroup[];
  specs: Record<string, SupportedParamSpec>;
}) {
  if (groups.length === 0) return null;
  return (
    <div className="space-y-1">
      {groups.map(group => (
        <p
          key={`${group.message}::${group.params.join(',')}`}
          data-testid="agent-param-suppression-note"
          data-params={group.params.join(',')}
          className="text-caption text-muted-foreground"
        >
          <span className="font-medium">
            {group.params.map(name => specs[name]?.label ?? name).join(', ')}
          </span>
          {': '}
          {group.message}
        </p>
      ))}
    </div>
  );
}

function formatLabel(value: string): string {
  return value
    .split('_')
    .map(part => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

/**
 * Narrow a provider id from the options payload to the SDK's ProviderType.
 *
 * These two sites used to carry `as AgentConfig['provider_type']` casts —
 * needed because the app-shell's private ProviderType union was MISSING
 * 'fireworks' (a live defect: the production default model's provider could not
 * be typed). With the local copy deleted and the SDK union as the single source,
 * the correct move is a real narrowing rather than a cast that silences the
 * checker without adding information.
 *
 * An unknown provider FAILS LOUDLY: the server sent a provider this client's SDK
 * does not know, which is a contract break worth surfacing, not one to coerce.
 */
const PROVIDER_TYPES: readonly ProviderType[] = [
  'openai',
  'groq',
  'lmstudio',
  'anthropic',
  'fireworks',
  'grok',
  'test',
];

function asProviderType(value: string): ProviderType {
  const match = PROVIDER_TYPES.find(p => p === value);
  if (!match) {
    throw new Error(
      `Unknown provider_type ${JSON.stringify(value)} — the API returned a provider ` +
        `this client's SDK does not define. Update @localagent/sdk's ProviderType.`,
    );
  }
  return match;
}

function buildConfigFromOptions(options: PersonaOptionsResponse): AgentConfig {
  const provider = options.providers[0];
  if (!provider) throw new Error('No providers returned from persona options');

  // Find the first provider that has models, falling back to the first provider with an empty model
  let selectedProvider = provider;
  let selectedModel = '';
  for (const p of options.providers) {
    const models = options.models_by_provider[p];
    if (models?.length) {
      selectedProvider = p;
      selectedModel = models[0];
      break;
    }
  }

  // A new agent INHERITS everything.
  //
  // This used to seed completion_params with every param that had a
  // default_value. That single line is why "presence in completion_params" could
  // not mean "the user pinned this": every agent was born fully pinned, so a
  // corrected default — e.g. the fix of Anthropic's temperature from a wrong 0.5 to the
  // vendor's 1.0 — could never reach a single existing agent.
  //
  // Empty now means inherit. The user pins by touching a control, and the pin is
  // then genuinely theirs rather than an artifact of the create form.
  return {
    system_prompt: options.system_prompt_templates[0] ?? '',
    provider_type: asProviderType(selectedProvider),
    model_name: selectedModel,
    completion_params: {},
    automatic_process_selection: options.automatic_process_selection,
    automatic_process_ids: options.automatic_process_ids,
  };
}

// pruneOverridesToSpec re-derives completion_params for a newly-selected model.
//
// It DROPS every param the new model's spec does not define — otherwise an
// orphaned param (e.g. `thinking`, valid only for a thinking-template model like
// deepseek-v4-flash) persists INVISIBLY: the Advanced Settings grid renders only
// controls for the new model's spec, so the stale param has no control yet still
// rides into the saved config and then hard-fails the backend's closed-world
// validator on EVERY turn (non-retryable AGENT_EXECUTE_FAILED).
//
// It does NOT seed defaults for params the new model adds. Its predecessor
// (`reconcileCompletionParams`, since deleted) did, and that half was wrong under
// sparse pins: seeding converts "inherit" into "pinned at today's default" on
// every model change, after which no later correction to that default can ever
// reach the agent.
//
// It RETURNS what it dropped, and the editor says so. Removing a value
// the user explicitly set, silently, is the same failure class this feature
// exists to remove — the drop is necessary, the silence was not.
//
// When the new model's spec is unknown (model not discovered on this client, e.g.
// an LM Studio model absent here), params are preserved as-is and the backend
// closed-world write guard remains the backstop.
//
// Exported for the functional story tier.
export function pruneOverridesToSpec(
  current: AgentCompletionParams,
  newSpec: Record<string, SupportedParamSpec> | undefined,
): { params: AgentCompletionParams; dropped: string[] } {
  if (!newSpec) return { params: current, dropped: [] };
  // completion_params is spec-driven and dynamic (thinking/top_k are not in the
  // static AgentCompletionParams shape), so walk it as a record.
  const cur = current as Record<string, unknown>;
  const kept: Record<string, unknown> = {};
  const dropped: string[] = [];
  for (const [name, value] of Object.entries(cur)) {
    if (name in newSpec) {
      kept[name] = value;
    } else {
      dropped.push(name);
    }
  }
  return { params: kept as AgentCompletionParams, dropped };
}

/**
 * Which spec-visible params this agent has PINNED.
 *
 * Presence in `completion_params` is the whole rule — there is no `params_mode`
 * column and no marker field. Everything absent inherits, and therefore
 * picks up a corrected default on the agent's next turn. Restricted to keys the
 * spec declares so an orphan (which `pruneOverridesToSpec` is about to remove
 * anyway) is never counted as a deliberate override.
 *
 * Exported for the functional story tier — the stories assert against the REAL
 * derivation, not a copy of it that can silently disagree.
 */
export function collectOverriddenParams(
  specParams: Record<string, SupportedParamSpec>,
  completionParams: AgentCompletionParams,
): string[] {
  return Object.keys(specParams).filter(name => name in completionParams);
}

/**
 * Return params to inheritance by REMOVING their keys.
 *
 * It never writes the current default back as a value. The two are
 * indistinguishable the instant they are done and behave in opposite ways
 * forever after: a re-pinned default is frozen at today's number, so the next
 * correction to that number will not reach this agent. Removing the key is the
 * only reset that means what "reset" says.
 *
 * Exported for the functional story tier.
 */
export function removeOverrides(
  current: AgentCompletionParams,
  paramNames: string[],
): AgentCompletionParams {
  const next = { ...current } as Record<string, unknown>;
  for (const name of paramNames) delete next[name];
  return next as AgentCompletionParams;
}

export interface SubmitBlocker {
  kind: 'missing' | 'loading';
  message: string;
}

/**
 * Why the save button is disabled, in the order the user should act on
 * (persona ADR-004 unaffected: no server bound is restated here).
 *
 * It EXPLAINS an already-computed disabled state and validates nothing of its
 * own — the six ANDed conditions were indistinguishable on screen, so a form
 * waiting on the param schema looked identical to one the user had filled in
 * wrongly.
 */
export function deriveSubmitBlockers(input: {
  name: string;
  systemPrompt: string;
  modelName: string;
  schemaFingerprint: string | undefined;
  formUnavailable: boolean;
  dataError: boolean;
}): SubmitBlocker[] {
  const blockers: SubmitBlocker[] = [];
  if (input.dataError) {
    blockers.push({ kind: 'missing', message: 'Agent data failed to load — see the error above.' });
  } else if (input.formUnavailable) {
    blockers.push({ kind: 'loading', message: 'Agent data is still loading.' });
  }
  if (!input.name.trim()) {
    blockers.push({ kind: 'missing', message: 'Name is required.' });
  }
  if (!input.modelName) {
    blockers.push({ kind: 'missing', message: 'Choose a model.' });
  }
  if (!input.systemPrompt.trim()) {
    blockers.push({ kind: 'missing', message: 'System Prompt is required.' });
  }
  if (!input.schemaFingerprint) {
    blockers.push({
      kind: 'loading',
      message: "Loading this model's parameter schema.",
    });
  }
  return blockers;
}

// Exported for the functional story tier — the story renders the REAL button
// and the REAL reason node, not a copy of the disabled predicate.
export function AgentSubmitAction({
  blockers,
  pending,
  label,
}: {
  blockers: SubmitBlocker[];
  pending: boolean;
  label: string;
}) {
  const blocker = blockers[0];
  return (
    <>
      {blocker && (
        <span
          data-testid="agent-submit-blocked-reason"
          data-blocker-kind={blocker.kind}
          className="self-center text-caption text-muted-foreground"
        >
          {blocker.message}
        </span>
      )}
      <Button type="submit" title={blocker?.message} disabled={Boolean(blocker) || pending}>
        {label}
      </Button>
    </>
  );
}

/**
 * The inherited/override affordance for one param row.
 *
 * Rendered by `ParamControl` for every param the editor drives from the resolved
 * schema. `inherited` is `undefined` for callers that render a control outside
 * the inheritance model (the constraint/thinking stories drive `ParamControl`
 * directly from a spec fixture with no defaults tier behind it) — those get no
 * badge and no `data-inherited` attribute at all, rather than a fabricated
 * "inherited" claim nothing computed.
 */
function ParamInheritanceBadges({
  paramName,
  inherited,
  onReset,
  disabled,
}: {
  paramName: string;
  inherited: boolean | undefined;
  onReset?: () => void;
  disabled: boolean;
}) {
  if (inherited === undefined) return null;
  if (inherited) {
    return (
      <span
        data-testid={`agent-${paramName}-inherited-badge`}
        className="rounded-full bg-muted px-2 py-0.5 text-micro font-medium uppercase tracking-wide text-muted-foreground"
      >
        Inherited
      </span>
    );
  }
  return (
    <>
      <span
        data-testid={`agent-${paramName}-override-badge`}
        className="rounded-full bg-primary/10 px-2 py-0.5 text-micro font-medium uppercase tracking-wide text-primary"
      >
        Overridden
      </span>
      {onReset && (
        <button
          type="button"
          data-testid={`agent-${paramName}-reset`}
          onClick={onReset}
          disabled={disabled}
          className="text-micro font-medium text-muted-foreground underline hover:text-foreground disabled:opacity-50"
        >
          Reset
        </button>
      )}
    </>
  );
}

// Exported for the functional story tier (AgentEditorParams.stories.tsx) —
// the stories render the REAL control from spec fixtures, mirroring how the
// Advanced Settings grid below wires it (same pattern as the search stories
// importing the real ResultRow).
export function ParamControl({ paramName, spec, value, disabled, onChange, inertReason, inherited, onReset }: {
  paramName: string;
  spec: SupportedParamSpec;
  value: unknown;
  disabled: boolean;
  onChange: (val: number | string) => void;
  // Presence makes the control inert; the message itself is stated once by
  // ParamSuppressionNotes (persona ADR-004).
  inertReason?: string;
  /**
   * `true` when this param has NO key in `completion_params` and the rendered
   * value therefore comes from the resolved defaults; `false` when the user has
   * pinned it. `undefined` for callers outside the inheritance model.
   *
   * An inherited value is shown as a GHOST — placeholder text, an unfilled track
   * — never as a filled control, which reads as a concrete value the user set.
   */
  inherited?: boolean;
  /**
   * Return this param to inheritance. The handler must REMOVE the key, never
   * write the current default back as a value — a param re-pinned at
   * today's default looks identical in the UI and behaves in the opposite way the
   * moment the default is corrected.
   */
  onReset?: () => void;
}) {
  const badges = (
    <ParamInheritanceBadges
      paramName={paramName}
      inherited={inherited}
      onReset={onReset}
      disabled={disabled}
    />
  );
  const dataInherited = inherited === undefined ? undefined : inherited ? 'true' : 'false';

  if (inertReason) {
    return (
      <div
        data-testid={`agent-param-${paramName}`}
        data-inert="true"
        data-inherited={dataInherited}
      >
        <div className="mb-2 flex items-center gap-2">
          <label className="block text-label font-medium text-muted-foreground">{spec.label}</label>
          {badges}
        </div>
        <div className="rounded-md bg-muted p-2 opacity-60">
          {/* The value is DISPLAYED, not discarded — it is still stored. */}
          <span data-testid={`agent-${paramName}-inert-value`} className="text-body">
            {value != null ? String(value) : '—'}
          </span>
        </div>
      </div>
    );
  }

  // A SLIDER ASSERTS A RANGE. It is therefore only correct when the server sent
  // one: `min`, `max` AND `step` all present.
  //
  // This used to read `min={spec.min ?? 0} max={spec.max ?? 1}`, which meant a
  // spec carrying no bounds — seeded Anthropic `top_p` is exactly that, because
  // the vendor publishes no recommended range — rendered as a 0..1
  // slider whose endpoints the backend does not enforce and whose granularity
  // nothing chose. The user could not express a legal value outside the invented
  // range, and believed a constraint that did not exist. Fabricating a
  // constraint is the mirror image of silently discarding one, and this feature
  // removes both.
  //
  // With no range to assert, the honest control is an unbounded numeric input:
  // it offers exactly what the server offers, and an out-of-policy value is
  // rejected by the backend in the backend's own words.
  const hasRange =
    typeof spec.min === 'number' &&
    typeof spec.max === 'number' &&
    typeof spec.step === 'number';
  const numVal =
    typeof value === 'number'
      ? value
      : typeof spec.default_value === 'number'
        ? spec.default_value
        : null;

  // Integer-typed sliders (e.g. `top_k` 0..100 step 1, model-fidelity) render
  // the same range control as numeric ones — only the label formatting and
  // parse differ (whole numbers, no decimals).
  if (
    spec.ui_hint === 'slider' &&
    (spec.type === 'numeric' || spec.type === 'integer') &&
    hasRange &&
    numVal !== null
  ) {
    const isInteger = spec.type === 'integer';
    const shown = isInteger ? String(Math.round(numVal)) : numVal.toFixed(2);
    const ghost = inherited === true;
    const span = spec.max! - spec.min!;
    const markerPercent = span > 0 ? ((numVal - spec.min!) / span) * 100 : 0;
    return (
      <div data-testid={`agent-param-${paramName}`} data-inherited={dataInherited}>
        <div className="mb-2 flex items-center gap-2">
          <label className="block text-label font-medium">
            {spec.label}
            {ghost ? (
              <span
                data-testid={`agent-${paramName}-default-hint`}
                className="ml-1 font-normal text-muted-foreground"
              >
                {shown}
              </span>
            ) : (
              `: ${shown}`
            )}
          </label>
          {badges}
        </div>
        <div className="relative">
          {ghost && (
            <span
              data-testid={`agent-${paramName}-default-marker`}
              aria-hidden="true"
              className="pointer-events-none absolute top-1/2 h-3 w-0.5 -translate-x-1/2 -translate-y-1/2 rounded-full bg-muted-foreground/60"
              style={{ left: `${markerPercent}%` }}
            />
          )}
          <input
            type="range"
            data-testid={`agent-${paramName}-slider`}
            data-ghost={ghost ? 'true' : undefined}
            min={spec.min!}
            max={spec.max!}
            step={spec.step!}
            value={numVal}
            onChange={e => onChange(isInteger ? parseInt(e.target.value, 10) : parseFloat(e.target.value))}
            disabled={disabled}
            className={`w-full ${ghost ? 'opacity-40 accent-transparent' : ''}`}
          />
        </div>
      </div>
    );
  }

  if (spec.ui_hint === 'segmented' && spec.type === 'enum' && spec.values && spec.labels) {
    const strVal = typeof value === 'string' ? value : (spec.default_value as string ?? spec.values[0]);
    return (
      <div data-testid={`agent-param-${paramName}`} data-inherited={dataInherited}>
        <div className="mb-2 flex items-center gap-2">
          <label className="block text-label font-medium">{spec.label}</label>
          {badges}
        </div>
        {/* The slider and input branches both carry a testid; this one did not,
            which left every segmented control (reasoning_effort, and now the
            normalized `thinking` switch) with no stable handle. A story asserts
            ABSENCE for models whose thinking mode is fixed, and an absence
            assertion needs a name to query. */}
        <div
          data-testid={`agent-${paramName}-segmented`}
          className="flex rounded-md bg-muted overflow-hidden"
        >
          {spec.values.map((v, i) => (
            <button
              key={v}
              type="button"
              onClick={() => onChange(v)}
              disabled={disabled}
              data-ghost={strVal === v && inherited === true ? 'true' : undefined}
              className={`flex-1 px-3 py-1.5 text-body transition-colors ${
                strVal === v
                  ? inherited === true
                    ? 'bg-background text-muted-foreground'
                    : 'bg-primary text-primary-foreground'
                  : 'text-foreground hover:bg-accent'
              } disabled:opacity-50`}
            >
              {spec.labels![i]}
            </button>
          ))}
        </div>
        {spec.description && (
          <p className="mt-1 text-caption text-muted-foreground">{spec.description}</p>
        )}
      </div>
    );
  }

  // Unbounded / non-slider control. This is where an unranged numeric param
  // lands, and it is the correct rendering rather than a degraded one: the
  // backend accepts any value its own policy allows, and so does this.
  return (
    <div data-testid={`agent-param-${paramName}`} data-inherited={dataInherited}>
      <div className="mb-2 flex items-center gap-2">
        <label className="block text-label font-medium">{spec.label}</label>
        {badges}
      </div>
      <ParamTextInput
        paramName={paramName}
        spec={spec}
        value={value}
        disabled={disabled}
        onChange={onChange}
        inherited={inherited}
      />
      {spec.description && (
        <p className="mt-1 text-caption text-muted-foreground">{spec.description}</p>
      )}
    </div>
  );
}

/** Parse a typed string back into the scalar the spec's `type` declares. */
function parseParamText(text: string, type: string): number | string | undefined {
  if (type === 'integer') {
    const n = parseInt(text, 10);
    return Number.isNaN(n) ? undefined : n;
  }
  if (type === 'numeric') {
    const n = parseFloat(text);
    return Number.isNaN(n) ? undefined : n;
  }
  return text;
}

/**
 * The free-text arm of `ParamControl`.
 *
 * It owns its own text state on purpose. Deriving the input's `value` straight
 * from the config makes intermediate keystrokes unrepresentable — "0." and "" do
 * not parse, so they can never be written back, so the field appears frozen and
 * a numeric param can never be cleared. Local text plus a parse on the way out
 * is what makes an UNBOUNDED numeric param (Anthropic `top_p`, which publishes
 * no range) genuinely editable, which is the whole point of routing unranged
 * params here instead of onto an invented slider.
 *
 * It resyncs from `value` only when the incoming value is not the one this
 * field's own text produces — so a reset, a model change, or a fetched config
 * lands, while the user's in-progress typing is never rewritten under them.
 */
function ParamTextInput({ paramName, spec, value, disabled, onChange, inherited }: {
  paramName: string;
  spec: SupportedParamSpec;
  value: unknown;
  disabled: boolean;
  onChange: (val: number | string) => void;
  inherited?: boolean;
}) {
  const ghost = inherited === true;
  const [text, setText] = useState(ghost || value == null ? '' : String(value));

  useEffect(() => {
    if (ghost) {
      setText('');
      return;
    }
    setText(prev => (parseParamText(prev, spec.type) === value ? prev : value != null ? String(value) : ''));
  }, [value, spec.type, ghost]);

  return (
    <input
      type="text"
      inputMode={spec.type === 'integer' || spec.type === 'numeric' ? 'decimal' : 'text'}
      data-testid={`agent-${paramName}-input`}
      data-ghost={ghost ? 'true' : undefined}
      placeholder={ghost && value != null ? String(value) : undefined}
      value={text}
      onChange={e => {
        setText(e.target.value);
        const parsed = parseParamText(e.target.value, spec.type);
        if (parsed !== undefined) onChange(parsed);
      }}
      disabled={disabled}
      className="w-full rounded-md bg-muted px-3 py-2 text-body focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-50"
    />
  );
}

export interface AgentEditorFormProps {
  personaId?: string;
  personaName?: string;
  onSaved?: (personaId?: string) => void;
  onCancel?: () => void;
  readOnly?: boolean;
  initialData?: { name: string; description: string; groupName: string; config: AgentConfig };
  onCopy?: () => void;
}

export function PersonaAutomaticProcessSettings({
  config,
  processes,
  disabled,
  onChange,
}: {
  config: AgentConfig;
  processes: AgentProcess[];
  disabled: boolean;
  onChange: (config: AgentConfig) => void;
}) {
  return (
    <>
      <label className="mb-4 flex items-center gap-2 text-body">
        <input
          data-testid="agent-automatic-process-selection"
          type="checkbox"
          checked={config.automatic_process_selection}
          onChange={event => onChange({ ...config, automatic_process_selection: event.target.checked })}
          disabled={disabled}
        />
        Choose a process automatically for each message
      </label>
      <select
        data-testid="agent-default-process-select"
        value={config.agent_process_id ?? ''}
        onChange={event => {
          const next = { ...config };
          if (event.target.value) next.agent_process_id = event.target.value;
          else delete next.agent_process_id;
          onChange(next);
        }}
        disabled={disabled || config.automatic_process_selection}
        className="w-full rounded-md bg-muted px-3 py-2 text-body focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-50"
      >
        <option value="">None (Simple)</option>
        {processes.map(process => <option key={process.id} value={process.id}>{process.name}</option>)}
      </select>
      <fieldset className="mt-5" disabled={disabled}>
        <legend className="text-label font-medium">Processes automatic mode may choose</legend>
        <p className="mb-2 mt-1 text-caption text-muted-foreground">
          Assign the processes this agent may consider. An empty assignment makes automatic turns fail visibly.
        </p>
        <div className="grid gap-2 sm:grid-cols-2">
          {processes.map(process => (
            <label key={process.id} className="flex items-center gap-2 text-body">
              <input
                type="checkbox"
                data-testid={`agent-automatic-process-${process.id}`}
                checked={config.automatic_process_ids.includes(process.id)}
                onChange={event => onChange({
                  ...config,
                  automatic_process_ids: event.target.checked
                    ? [...config.automatic_process_ids, process.id]
                    : config.automatic_process_ids.filter(id => id !== process.id),
                })}
              />
              {process.name}
            </label>
          ))}
        </div>
      </fieldset>
    </>
  );
}

export function AgentEditorForm({ personaId, personaName, onSaved, onCancel, readOnly, initialData, onCopy }: AgentEditorFormProps) {
  const navigate = useNavigate();
  const isEditMode = Boolean(personaId || personaName);
  const createAgent = useCreateAgentDefinition();
  const updateAgent = useUpdateAgentDefinition();
  const createGroup = useCreateAgentGroup();
  const effectiveId = personaId;
  const { data: existingPersona, isLoading: personaLoading } = useAgentDefinitionById(readOnly ? undefined : effectiveId);
  const configQuery = useAgentDefinitionConfigById(readOnly ? undefined : effectiveId);
  const optionsQuery = useAgentOptions();
  const personaOptions = optionsQuery.data;
  // Default-process picker — process UI, gated on
  // the dev toggle: toggle off → today's persona editor unchanged.
  const { data: agentProcesses } = useAgentProcesses();
  const providers = useMemo(() => personaOptions?.providers ?? [], [personaOptions?.providers]);

  const modelsByProvider = useMemo(
    () => personaOptions?.models_by_provider ?? {},
    [personaOptions?.models_by_provider]
  );

  const personaGroups = personaOptions?.persona_groups ?? [];

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [groupName, setGroupName] = useState('');
  const [isCreatingGroup, setIsCreatingGroup] = useState(false);
  const [newGroupError, setNewGroupError] = useState('');
  const [config, setConfig] = useState<AgentConfig | null>(null);
  const [showSaved, setShowSaved] = useState(false);
  const [showAdvancedModel, setShowAdvancedModel] = useState(false);
  const hasInitializedCreateConfig = useRef(false);
  // The pins a model/provider change removed, so the user learns what they lost
  // Cleared on the next model change, not on save — the sentence has to
  // survive long enough to be read.
  const [droppedOverrides, setDroppedOverrides] = useState<string[]>([]);
  // A 409 `catalog_schema_stale` from a config write. Kept apart from
  // `saveError` because the remedy is different in kind: nothing the user typed
  // is wrong, so there is no field to correct — the schema underneath moved.
  const [schemaStale, setSchemaStale] = useState(false);
  // The server's own words for a rejected save, rendered verbatim. This used to
  // be a bare `console.error`, so a rejected save looked to the user exactly like
  // a successful one that had not navigated yet.
  const [saveError, setSaveError] = useState('');

  // Read-only mode: populate from initialData
  useEffect(() => {
    if (!readOnly || !initialData) return;
    setName(initialData.name);
    setDescription(initialData.description);
    setGroupName(initialData.groupName);
    setIsCreatingGroup(false);
    setConfig(initialData.config);
  }, [readOnly, initialData]);

  // Edit mode: initialize once per opened persona; later refetches never overwrite edits.
  const initializedMetadataFor = useRef<string | null>(null);
  const initializedGroupFor = useRef<string | null>(null);
  const initializedConfigFor = useRef<string | null>(null);

  useEffect(() => {
    if (!existingPersona || !('name' in existingPersona)) return;
    if (initializedMetadataFor.current === existingPersona.id) return;
    initializedMetadataFor.current = existingPersona.id;
    setName(existingPersona.name);
    setDescription(existingPersona.description || '');
    setIsCreatingGroup(false);
  }, [existingPersona]);

  useEffect(() => {
    if (!existingPersona || !('name' in existingPersona) || !personaOptions) return;
    if (initializedGroupFor.current === existingPersona.id) return;
    initializedGroupFor.current = existingPersona.id;
    const group = existingPersona.group_id
      ? personaOptions.persona_groups.find(g => g.id === existingPersona.group_id)
      : undefined;
    setGroupName(group?.name ?? '');
  }, [existingPersona, personaOptions]);

  useEffect(() => {
    if (!isEditMode || !effectiveId || !configQuery.data) return;
    if (initializedConfigFor.current === effectiveId) return;
    initializedConfigFor.current = effectiveId;
    setConfig(configQuery.data);
  }, [isEditMode, effectiveId, configQuery.data]);

  // Create mode: build config from server-provided options
  useEffect(() => {
    if (readOnly || isEditMode || !personaOptions) return;
    if (hasInitializedCreateConfig.current) return;
    setConfig(buildConfigFromOptions(personaOptions));
    hasInitializedCreateConfig.current = true;
  }, [readOnly, isEditMode, personaOptions]);

  // NOTE: the former silent auto-rewrite effect (model_name → providerModels[0]
  // when the saved model wasn't in the loaded options) is DELETED.
  // It hid an unavailable saved model — a saved
  // LM Studio model not discovered on this client was silently replaced. The
  // saved value is now shown as-is via AgentModelPicker's "unavailable here"
  // state; provider changes blank the model to force an explicit choice
  // (handleProviderChange below), never a list-order pick.

  // Lifecycle status per model for the CURRENT provider, straight from the
  // options payload. The server derived it; this only reshapes it into
  // the map the picker takes, so no clock or date comparison lands in the client.
  const modelStatuses = useMemo(() => {
    const providerCaps: Record<string, ModelCapabilitiesInfo> =
      personaOptions?.model_capabilities?.[config?.provider_type ?? ''] ?? {};
    const out: Record<string, ModelLifecycleStatus> = {};
    for (const [name, info] of Object.entries(providerCaps)) {
      if (info.status) out[name] = info.status;
    }
    return out;
  }, [personaOptions?.model_capabilities, config?.provider_type]);

  // Resolve model capabilities from persona options
  const currentModelCapabilities: ModelCapabilitiesInfo | null = useMemo(() => {
    if (!config || !personaOptions?.model_capabilities) return null;
    const providerCaps = personaOptions.model_capabilities[config.provider_type];
    if (!providerCaps) return null;
    return providerCaps[config.model_name] ?? null;
  }, [config?.provider_type, config?.model_name, personaOptions?.model_capabilities]);

  const providerModels = config ? (modelsByProvider[config.provider_type] ?? []) : [];
  const completionParamsState = config?.completion_params ?? {};

  /**
   * THE PARAM GRID'S SOURCE.
   *
   * Which controls exist, what they are bounded by, what they default to, and
   * where the model sits in its lifecycle all come from `GET /param-schema` —
   * the SAME server-side resolver the three write guards run. It used to come
   * from `getOptions()`'s `model_capabilities`, which is a SECOND projection of
   * the same data; an editor rendering from a second projection can offer a
   * control the backend rejects, and no amount of care on either side prevents
   * it, because the two derivations are free to drift. `model_capabilities`
   * remains the right source for the model PICKER (it lists every model) and for
   * the capabilities panel — just not for the controls the user writes through.
   */
  const schemaQuery = useParamSchema(config?.provider_type, config?.model_name);
  const paramSchema = schemaQuery.data;

  const sortedParams = useMemo(() => {
    if (!paramSchema) return [];
    return Object.entries(paramSchema.params).sort(
      ([, a], [, b]) => (a.sort_order ?? 0) - (b.sort_order ?? 0),
    );
  }, [paramSchema]);

  const overriddenParams = useMemo(
    () => (paramSchema ? collectOverriddenParams(paramSchema.params, completionParamsState) : []),
    [paramSchema, completionParamsState],
  );

  // Error state: config or options failed to load
  const hasDataError = isEditMode
    ? configQuery.isError || (configQuery.isSuccess && configQuery.data === null)
    : optionsQuery.isError;

  const formDisabled = readOnly || hasDataError || !config;

  const submitBlockers = useMemo(
    () =>
      deriveSubmitBlockers({
        name,
        systemPrompt: config?.system_prompt ?? '',
        modelName: config?.model_name ?? '',
        schemaFingerprint: paramSchema?.fingerprint,
        formUnavailable: Boolean(formDisabled),
        dataError: Boolean(hasDataError),
      }),
    [
      name,
      config?.system_prompt,
      config?.model_name,
      paramSchema?.fingerprint,
      formDisabled,
      hasDataError,
    ],
  );

  // Reset — see `removeOverrides` for why this removes the key rather than
  // writing the default back as a value.
  const resetParams = (paramNames: string[]) => {
    setConfig(prev =>
      prev
        ? { ...prev, completion_params: removeOverrides(prev.completion_params ?? {}, paramNames) }
        : prev,
    );
  };

  /**
   * Prune the pins to a newly-selected spec and SAY what was dropped.
   *
   * The spec consulted here is `getOptions()`'s `model_capabilities` rather than
   * `GET /param-schema`, for one reason: the prune must be synchronous with the
   * selection. The schema for the model the user just picked has not been
   * fetched yet at this instant, and leaving unsupported pins in the config until
   * it arrives means a save in that window carries params the new model rejects.
   * The two are projections of one resolver and agree by construction,
   * which is exactly the property that makes this substitution safe — if it ever
   * stops holding, the resolver-agreement test is what fails, not this silently.
   */
  const applyPrunedSpec = (
    current: AgentConfig,
    updates: Partial<AgentConfig>,
    newSpec: Record<string, SupportedParamSpec> | undefined,
  ) => {
    const { params, dropped } = pruneOverridesToSpec(current.completion_params ?? {}, newSpec);
    setDroppedOverrides(dropped);
    setConfig(prev => (prev ? { ...prev, ...updates, completion_params: params } : prev));
  };

  const handleProviderChange = (value: string) => {
    if (!config) return;
    const provider = asProviderType(value);
    const models = modelsByProvider[provider] ?? [];
    // Blank the model unless the current one is still valid for the new provider
    // — a provider change must force an EXPLICIT model choice, never silently
    // pick models[0]. Save stays
    // blocked (see the submit button's disabled condition) until one is chosen.
    const keepModel = models.includes(config.model_name);
    if (keepModel) {
      // Same model id under the new provider — prune params against the NEW
      // provider's spec (specs can differ across providers), same as a model
      // change, so orphans don't survive a provider switch.
      const newSpec = personaOptions?.model_capabilities?.[provider]?.[config.model_name]?.supported_params;
      applyPrunedSpec(config, { provider_type: provider }, newSpec);
    } else {
      setConfig(prev => prev ? { ...prev, provider_type: provider, model_name: '' } : prev);
      setDroppedOverrides([]);
    }
  };

  // Changing the model must PRUNE completion_params to the new model's spec, not
  // just rewrite model_name (the prior behavior). Otherwise a param the new model
  // doesn't support (e.g. `thinking` when switching off a thinking-capable model)
  // stays in the config with no control to show or clear it, and then hard-fails
  // the backend's closed-world validator on every turn. What it does NOT do is
  // seed the new model's defaults: under sparse pins that would silently convert
  // "inherit" into "pinned at today's default" on every model change.
  const handleModelChange = (value: string) => {
    if (!config) return;
    const newSpec = personaOptions?.model_capabilities?.[config.provider_type]?.[value]?.supported_params;
    applyPrunedSpec(config, { model_name: value }, newSpec);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!config) return;
    setSaveError('');
    setSchemaStale(false);

    /*
     * NOTHING IS RE-VALIDATED HERE, AND THAT IS THE POINT.
     *
     * `completion_params` is written only by the controls, each of which
     * renders exactly what the resolved schema declares. An out-of-policy value
     * is sent, refused by the one authority on the matter, and the refusal is
     * shown in the backend's own words (see the catch below).
     */
    try {
      const editId = personaId ?? existingPersona?.id;
      if (isEditMode) {
        if (!editId) {
          console.error('Cannot update agent: no persona ID available');
          return;
        }
        if (!paramSchema?.fingerprint) {
          setSaveError('Parameter schema is still loading — wait a moment and save again.');
          return;
        }
        await updateAgent.mutateAsync({
          id: editId,
          updates: {
            name: name !== existingPersona?.name ? name : undefined,
            description: description || undefined,
            group_name: groupName || undefined,
          },
          agentConfig: config,
          fingerprint: paramSchema.fingerprint,
        });
        setShowSaved(true);
        setTimeout(() => setShowSaved(false), 2000);
        onSaved?.();
      } else {
        if (!paramSchema?.fingerprint) {
          setSaveError('Parameter schema is still loading — wait a moment and save again.');
          return;
        }
        const result = await createAgent.mutateAsync({
          name,
          agentConfig: config,
          description: description || undefined,
          groupName: groupName || undefined,
          fingerprint: paramSchema.fingerprint,
        });
        onSaved?.(result.persona_id);
      }
    } catch (error) {
      if (!(error instanceof Error)) throw error;
      // A 409 is `catalog_schema_stale`: the request was valid against a
      // schema that no longer exists. It is NOT a param-level rejection, so it
      // must not be rendered as one; the user would go looking for a bad value
      // that is not there. The remedy is a reload, and that is what is offered.
      if (isConflict(error)) {
        setSchemaStale(true);
        return;
      }
      // Everything else is shown VERBATIM. This used to be a bare console.error,
      // which meant a rejected save was indistinguishable, on screen, from one
      // that had succeeded and simply not navigated yet.
      setSaveError(error.message);
    }
  };

  // Show loading while fetching persona/config/options, or while useEffect hasn't populated config yet
  const showLoading =
    !readOnly && (
      (isEditMode ? (personaLoading || configQuery.isLoading) : optionsQuery.isLoading) ||
      (!hasDataError && !config)
    );

  if (showLoading) {
    return (
      <LoadingState label="Loading agent..." className="h-full" />
    );
  }

  return (
    <form onSubmit={(e) => { void handleSubmit(e); }} className="flex h-full flex-col overflow-hidden">
      <div className="min-h-0 flex-1">
        <PageShell
          title={readOnly ? name : isEditMode ? 'Edit Agent' : 'Create Agent'}
          description={readOnly ? 'System Agent' : undefined}
          headerActions={
            showSaved ? <span className="text-label font-medium text-success-text">Saved</span> : undefined
          }
        >
        <div className="space-y-6">
          {hasDataError && (
            <div
              data-testid="agent-data-error"
              className="rounded-md bg-destructive/10 p-3 text-label text-destructive"
            >
              {isEditMode
                ? 'Failed to load agent configuration. Form fields are disabled.'
                : 'Failed to load agent options from the server. Form fields are disabled.'}
            </div>
          )}

          {schemaStale && (
            <SchemaStaleNotice
              onReload={() => {
                setSchemaStale(false);
                initializedConfigFor.current = null;
                void schemaQuery.refetch();
                void configQuery.refetch();
              }}
            />
          )}

          {saveError && (
            <div
              data-testid="agent-save-error"
              className="rounded-md bg-destructive/10 p-3 text-label text-destructive"
            >
              {saveError}
            </div>
          )}

          {/* Basic Info */}
          <div className="rounded-lg bg-card p-6">
            <h2 className="mb-4 text-title-2 font-semibold">Basic Information</h2>

            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="mb-2 block text-label font-medium">
                    Name <span className="text-destructive">*</span>
                  </label>
                  <input
                    type="text"
                    value={name}
                    onChange={e => setName(e.target.value)}
                    required
                    disabled={formDisabled}
                    className="w-full rounded-md bg-muted px-3 py-2 text-label focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-50"
                    placeholder="e.g., Code Reviewer"
                  />
                </div>

                <div>
                  <label className="mb-2 block text-label font-medium">Group</label>
                  {isCreatingGroup ? (
                    <div>
                      <div className="flex gap-2">
                        <input
                          type="text"
                          data-testid="agent-group-name-input"
                          value={groupName}
                          onChange={e => { setGroupName(e.target.value); setNewGroupError(''); }}
                          onKeyDown={e => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              if (!groupName.trim()) return;
                              createGroup.mutate({ name: groupName.trim() }, {
                                onSuccess: (group) => { setGroupName(group.name); setIsCreatingGroup(false); setNewGroupError(''); },
                                onError: (err) => { setNewGroupError(err.message || 'Failed to create group'); },
                              });
                            }
                          }}
                          disabled={formDisabled || createGroup.isPending}
                          autoFocus
                          className="w-full rounded-md bg-muted px-3 py-2 text-body focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-50"
                          placeholder="New group name..."
                        />
                        <button
                          type="button"
                          data-testid="agent-group-confirm-button"
                          onClick={() => {
                            if (!groupName.trim()) return;
                            createGroup.mutate({ name: groupName.trim() }, {
                              onSuccess: (group) => { setGroupName(group.name); setIsCreatingGroup(false); setNewGroupError(''); },
                              onError: (err) => { setNewGroupError(err.message || 'Failed to create group'); },
                            });
                          }}
                          disabled={formDisabled || createGroup.isPending || !groupName.trim()}
                          className="shrink-0 rounded-md px-2 text-body text-primary hover:bg-primary/10"
                        >
                          {createGroup.isPending ? 'Creating...' : 'Confirm'}
                        </button>
                        <button
                          type="button"
                          data-testid="agent-group-cancel-button"
                          onClick={() => { setIsCreatingGroup(false); setGroupName(''); setNewGroupError(''); }}
                          className="shrink-0 rounded-md px-2 text-body text-muted-foreground hover:bg-accent hover:text-foreground"
                        >
                          Cancel
                        </button>
                      </div>
                      {newGroupError && (
                        <p className="mt-1 text-caption text-destructive">{newGroupError}</p>
                      )}
                    </div>
                  ) : (
                    <select
                      data-testid="agent-group-select"
                      value={groupName}
                      onChange={e => {
                        if (e.target.value === '__create_new__') {
                          setGroupName('');
                          setIsCreatingGroup(true);
                        } else {
                          setGroupName(e.target.value);
                        }
                      }}
                      disabled={formDisabled}
                      className="w-full rounded-md bg-muted px-3 py-2 text-label focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-50"
                    >
                      <option value="">No group</option>
                      {personaGroups.map(group => (
                        <option key={group.id} value={group.name}>{group.name}</option>
                      ))}
                      <option value="__create_new__">+ Create new group</option>
                    </select>
                  )}
                </div>
              </div>

              <div>
                <label className="mb-2 block text-label font-medium">Description</label>
                <textarea
                  value={description}
                  onChange={e => setDescription(e.target.value)}
                  rows={2}
                  disabled={formDisabled}
                  className="w-full rounded-md bg-muted px-3 py-2 text-label focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-50"
                  placeholder="Brief description of this agent..."
                />
              </div>
            </div>
          </div>

          {/* System Prompt */}
          <div className="rounded-lg bg-card p-6">
            <div className="mb-4 flex items-center gap-2">
              <h2 className="text-title-2 font-semibold">System Prompt</h2>
              <Popover
                aria-label="What this agent sees at runtime"
                triggerClassName="h-5 w-5 bg-muted text-caption text-muted-foreground hover:text-foreground"
                contentClassName="w-80"
                trigger={<span data-testid="agent-context-explainer-trigger">?</span>}
              >
                <div data-testid="agent-context-explainer" className="space-y-2">
                  <p className="text-label font-medium">Agent context</p>
                  <p className="text-caption text-muted-foreground">
                    What this agent sees at runtime is built from its{' '}
                    <strong>System Prompt</strong>, the conversation itself, per-workspace
                    instructions in the workspace&apos;s <code>.shogunchop/</code> steering files
                    and <code>AGENTS.md</code>, and the steps of its{' '}
                    <strong>Default Process</strong>.
                  </p>
                </div>
              </Popover>
            </div>
            <textarea
              data-testid="agent-system-prompt"
              value={config?.system_prompt ?? ''}
              onChange={e => config && setConfig({ ...config, system_prompt: e.target.value })}
              rows={10}
              disabled={formDisabled}
              className="w-full rounded-md bg-muted px-3 py-2 font-mono text-body focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-50"
              placeholder="You are an expert AI assistant..."
            />
            <p data-testid="agent-system-prompt-counter" className="mt-2 text-caption text-muted-foreground">
              {(config?.system_prompt ?? '').length.toLocaleString()} characters
              {currentModelCapabilities && (
                <>
                  {' · '}
                  <span data-testid="agent-system-prompt-context-window">
                    {currentModelCapabilities.context_window.toLocaleString()}-token context window
                  </span>
                </>
              )}
            </p>
          </div>

          {/* Model Configuration */}
          <div className="rounded-lg bg-card p-6">
            <h2 className="mb-4 text-title-2 font-semibold">Model Configuration</h2>

            <div className="space-y-4">
              <AgentModelPicker
                providers={providers}
                models={providerModels}
                provider={config?.provider_type ?? ''}
                model={config?.model_name ?? ''}
                onProviderChange={handleProviderChange}
                onModelChange={handleModelChange}
                disabled={formDisabled}
                formatProviderLabel={formatLabel}
                modelStatuses={modelStatuses}
              />

              {/*
                The model's lifecycle, stated where the user is choosing
                it. Nothing here disables a control or blocks the save:
                the user may have a deliberate reason to stay, and a save that
                silently refuses teaches them nothing while costing them the edit.
              */}
              {config && paramSchema && (
                <ModelDeprecationWarning
                  modelName={config.model_name}
                  status={paramSchema.model.status}
                  deprecationDate={paramSchema.model.deprecation_date}
                  onFindReplacement={() => {
                    // Same reassign path DeleteEntryDialog owns. Query
                    // params land on Providers with the row focused and open the
                    // dialog; nothing here blocks the save.
                    const q = new URLSearchParams({
                      tab: 'providers',
                      provider: config.provider_type,
                      model: config.model_name,
                      reassign: '1',
                    });
                    void navigate(`/agents?${q.toString()}`);
                  }}
                />
              )}

              {droppedOverrides.length > 0 && (
                <DroppedOverridesNotice dropped={droppedOverrides} />
              )}

              {config && (
                <ModelCapabilitiesDisplay
                  capabilities={currentModelCapabilities}
                  loading={optionsQuery.isLoading}
                />
              )}

              <button
                type="button"
                onClick={() => setShowAdvancedModel(prev => !prev)}
                className="flex items-center gap-1 text-body text-muted-foreground hover:text-foreground transition-colors"
              >
                <span className="inline-block transition-transform" style={{ transform: showAdvancedModel ? 'rotate(90deg)' : 'rotate(0deg)' }}>&#9654;</span>
                Advanced Settings
              </button>

              {showAdvancedModel && (
                <>
                  {/*
                    Sampling is owned by LM Studio itself, where a quantized-model
                    operator already tunes it alongside quantization.
                    Saying so matters: an editor
                    that simply shows fewer controls reads as a bug or a
                    discovery failure.
                  */}
                  {config?.provider_type === 'lmstudio' && (
                    <p
                      data-testid="agent-lmstudio-sampling-hint"
                      className="text-caption text-muted-foreground"
                    >
                      Sampling (temperature, top-p, repeat penalty) is configured in LM Studio
                      itself, alongside the model&apos;s quantization and context length. Load and
                      tune the model there; this agent controls the prompt, tools and output
                      length.
                    </p>
                  )}
                  {schemaQuery.isError && (
                    <p
                      data-testid="agent-schema-error"
                      className="rounded-md bg-destructive/10 p-3 text-caption text-destructive"
                    >
                      Could not load this model&apos;s parameters:{' '}
                      {schemaQuery.error.message}
                    </p>
                  )}

                  {/*
                    How many of this agent's parameters have opted out of
                    inheritance, and one action to put them all back. Without the
                    count, "why did the platform-wide temperature correction not
                    reach this agent" has no answer visible anywhere in the UI.
                  */}
                  {overriddenParams.length > 0 && (
                    <ParamOverrideNotice
                      count={overriddenParams.length}
                      onResetAll={() => resetParams(overriddenParams)}
                      disabled={formDisabled}
                    />
                  )}

                  {sortedParams.length > 0 && paramSchema && (() => {
                    // Effective values for rule evaluation: pins win over
                    // resolved defaults. Suppression is computed from THIS map
                    // so turning reasoning on/off flips inertness live.
                    const effectiveForRules: Record<string, unknown> = {
                      ...paramSchema.defaults,
                      ...(completionParamsState as Record<string, unknown>),
                    };
                    const suppressed = suppressedParamsFromRules(
                      paramSchema.rules,
                      effectiveForRules,
                    );
                    return (
                      <>
                      <div className="grid grid-cols-2 gap-4">
                        {sortedParams.map(([paramName, spec]) => {
                          // Presence in completion_params IS the pin. An
                          // inherited param renders the resolved default so the
                          // user sees the value in force — showing it does not pin
                          // it; only touching the control does.
                          const pinned = paramName in completionParamsState;
                          const effectiveValue = pinned
                            ? (completionParamsState as Record<string, unknown>)[paramName]
                            : paramSchema.defaults[paramName];
                          return (
                            <ParamControl
                              key={paramName}
                              paramName={paramName}
                              spec={spec}
                              value={effectiveValue}
                              inherited={!pinned}
                              onReset={pinned ? () => resetParams([paramName]) : undefined}
                              disabled={formDisabled}
                              inertReason={suppressed.reasons[paramName]}
                              onChange={(val) =>
                                setConfig(prev => prev ? {
                                  ...prev,
                                  completion_params: { ...completionParamsState, [paramName]: val },
                                } : prev)
                              }
                            />
                          );
                        })}
                      </div>
                      <ParamSuppressionNotes
                        groups={suppressed.groups}
                        specs={paramSchema.params}
                      />
                      </>
                    );
                  })()}

                </>
              )}
            </div>
          </div>

          {/* Default Agent Process */}
          <div className="rounded-lg bg-card p-6">
            <h2 className="mb-4 text-title-2 font-semibold">Default Process</h2>
            <p className="mb-3 text-caption text-muted-foreground">
              The agent process new sessions with this agent run by default. Leave unset to use
              the seeded Simple process.
            </p>
            {config && (
              <PersonaAutomaticProcessSettings
                config={config}
                processes={agentProcesses ?? []}
                disabled={formDisabled}
                onChange={setConfig}
              />
            )}
          </div>

        </div>
        </PageShell>
      </div>

      {/* Footer Actions */}
      <div className="border-t bg-background px-6 py-4">
        <div className="flex justify-end gap-3">
          {readOnly ? (
            <Button type="button" onClick={() => onCopy?.()}>
              Copy to My Agents
            </Button>
          ) : (
            <>
              <Button
                type="button"
                variant="outline"
                onClick={() => onCancel?.()}
                disabled={createAgent.isPending || updateAgent.isPending}
              >
                Cancel
              </Button>
              <AgentSubmitAction
                blockers={submitBlockers}
                pending={createAgent.isPending || updateAgent.isPending}
                label={
                  isEditMode
                    ? updateAgent.isPending ? 'Updating...' : 'Update Agent'
                    : createAgent.isPending ? 'Creating...' : 'Create Agent'
                }
              />
            </>
          )}
        </div>
      </div>
    </form>
  );
}

export function AgentEditorView() {
  useCatalogSubscription('persona');
  const { personaId } = useParams<{ personaId: string }>();
  const navigate = useNavigate();

  return (
    <div className="flex h-full flex-col">
      <AgentEditorForm
        personaId={personaId}
        onSaved={() => { void navigate('/agents'); }}
        onCancel={() => { void navigate('/agents'); }}
      />
    </div>
  );
}
